

(function() {
  'use strict';
  
  class CustomProductCard {
    constructor(card) {
      this.card = card;
      this.variantPills = card.querySelectorAll('.variant-pill');
      this.qtyInput = card.querySelector('.qty-input');
      this.qtyDecrease = card.querySelector('.qty-decrease');
      this.qtyIncrease = card.querySelector('.qty-increase');
      this.atcBtn = card.querySelector('.atc-btn');
      this.atcBtnText = card.querySelector('.add-to-cart-text');
      this.priceDisplay = card.querySelector('[data-price-display]');
      this.comparePriceDisplay = card.querySelector('[data-compare-price-display]');
      this.saveBadge = card.querySelector('[data-save-badge]');
      this.mainImage = card.querySelector('[data-main-image]');

      this.pincodeGated = card.dataset.pincodeGated === 'true';
      this.isAdding = false;

      if (this.isPincodeVerified() && this.atcBtnText) {
        this.atcBtnText.textContent = 'Add to cart';
      }

      this.#createTooltip();
      this.init();
    }

    #createTooltip() {
      if (!this.atcBtn) return;
      const wrapper = document.createElement('div');
      wrapper.className = 'cpc-atc-wrapper';
      this.atcBtn.parentNode.insertBefore(wrapper, this.atcBtn);
      wrapper.appendChild(this.atcBtn);

      this.tooltip = document.createElement('div');
      this.tooltip.className = 'cpc-atc-tooltip';
      this.tooltip.setAttribute('role', 'alert');
      this.tooltip.setAttribute('aria-live', 'assertive');
      wrapper.appendChild(this.tooltip);
    }

    /** @param {string} [text] */
    #showTooltip(text) {
      if (!this.tooltip) return;
      this.tooltip.textContent = text || 'Not enough stock available';
      this.tooltip.classList.add('cpc-atc-tooltip--visible');
    }

    #hideTooltip() {
      this.tooltip?.classList.remove('cpc-atc-tooltip--visible');
    }

    init() {
      this.variantPills.forEach(pill => {
        pill.addEventListener('click', (e) => this.handleVariantChange(e));
      });

      this.qtyDecrease?.addEventListener('click', () => this.decreaseQty());
      this.qtyIncrease?.addEventListener('click', () => this.increaseQty());
      this.qtyInput?.addEventListener('change', (e) => this.validateQty(e));
      this.atcBtn?.addEventListener('click', () => this.handleAtcClick());
    }

    // Checked live against localStorage on every call (see assets/pincode-gate.js)
    // instead of cached on the instance, so a card built before any pincode was
    // verified still unlocks the moment another product's popup verifies one.
    isPincodeVerified() {
      return this.pincodeGated && Boolean(window.PincodeGate?.getVerifiedPincode?.());
    }

    handleAtcClick() {
      // In-flight guard only - never touch the native `disabled` attribute for
      // this, since .atc-btn:disabled repaints the button grey (that's reserved
      // for genuinely blocked states like Sold Out).
      if (this.isAdding) return;

      if (this.pincodeGated && !this.isPincodeVerified()) {
        const dialog = document.getElementById('pincode-gate-dialog');
        if (!dialog || typeof dialog.open !== 'function') {
          // Dialog unavailable for some reason - fail closed rather than skip the gate.
          return;
        }

        // No onValidated callback needed here: the dialog broadcasts
        // 'pincode-gate:verified' on success (assets/pincode-gate-dialog.js),
        // and the document-level listener below refreshes every gated card's
        // button label - including this one - as soon as that fires.
        dialog.open();
        return;
      }

      this.addToCart();
    }

    handleVariantChange(e) {
      const pill = e.target;
      if (pill.classList.contains('disabled')) return;

      this.variantPills.forEach(p => p.classList.remove('active'));
      pill.classList.add('active');

      const price = parseInt(pill.dataset.price);
      const comparePrice = parseInt(pill.dataset.comparePrice);
      
      this.priceDisplay.textContent = `₹${Math.round(price / 100)}`;
      
      if (comparePrice > price) {
        this.priceDisplay.classList.add('solution-card__price--sale');
        this.comparePriceDisplay.textContent = `₹${Math.round(comparePrice / 100)}`;
        this.comparePriceDisplay.style.display = 'inline';

        if (this.saveBadge) {
          const savingsPercent = Math.round(((comparePrice - price) / comparePrice) * 100);
          this.saveBadge.textContent = `Save ${savingsPercent}%`;
          this.saveBadge.style.display = savingsPercent > 0 ? '' : 'none';
        }
      } else {
        this.priceDisplay.classList.remove('solution-card__price--sale');
        this.comparePriceDisplay.style.display = 'none';

        if (this.saveBadge) {
          this.saveBadge.style.display = 'none';
        }
      }

      const imageSrc = pill.dataset.imageSrc;
      if (imageSrc && this.mainImage) {
        this.mainImage.src = imageSrc;
      }

      this.atcBtn.dataset.variantId = pill.dataset.variantId;
      this.atcBtn.disabled = pill.dataset.available === 'false';

      if (pill.dataset.available !== 'true') {
        this.atcBtnText.textContent = 'Sold Out';
      } else if (this.pincodeGated && !this.isPincodeVerified()) {
        this.atcBtnText.textContent = 'Check Availability';
      } else {
        this.atcBtnText.textContent = 'Add to cart';
      }
    }

    decreaseQty() {
      const currentQty = parseInt(this.qtyInput.value);
      if (currentQty > 1) {
        this.qtyInput.value = currentQty - 1;
      }
    }

    increaseQty() {
      const currentQty = parseInt(this.qtyInput.value);
      const max = parseInt(this.qtyInput.max);
      if (currentQty < max) {
        this.qtyInput.value = currentQty + 1;
      }
    }

    validateQty(e) {
      let value = parseInt(e.target.value);
      const min = parseInt(e.target.min);
      const max = parseInt(e.target.max);
      
      if (isNaN(value) || value < min) value = min;
      if (value > max) value = max;
      
      e.target.value = value;
    }

    async addToCart() {
      const variantId = this.atcBtn.dataset.variantId;
      const quantity = parseInt(this.qtyInput.value);

      this.isAdding = true;
      this.#hideTooltip();

      // Same built-in feedback as the PDP add-to-cart button (assets/product-form.js
      // AddToCartComponent#handleClick): fly-to-cart image + checkmark-burst fire
      // immediately on click, not gated behind the network response. The idle
      // label itself is never rewritten - it just gets covered by the animation.
      this.#animateFlyToCart();
      const animationDone = this.#playAddedAnimation();

      let cartUpdated = false;

      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: quantity }]
          })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.description || 'Failed to add to cart');
        }

        const cart = await this.refreshCartDrawer();
        const cartDrawer = document.querySelector('cart-drawer-component');

        // tt-custom-code for : stop - cart drawer open default when ATC
        // if (cartDrawer?.hasAttribute('auto-open')) {
        //   this.#dispatchCartUpdate(cart);
        // } else {
        //   this.openCartDrawer();
        //   requestAnimationFrame(() => this.#dispatchCartUpdate(cart));
        // }
        this.#dispatchCartUpdate(cart);
        cartUpdated = true;
      } catch (error) {
        const msg = error instanceof Error ? error.message : '';
        const match = msg.match(/\d+/);
        this.#showTooltip(msg || undefined);

        if (match) {
          // Partial success — Shopify added what was available and returned 422 with the count
          const cart = await this.refreshCartDrawer();
          const cartDrawer = document.querySelector('cart-drawer-component');
          // tt-custom-code for : stop - cart drawer open default when ATC
          // if (cartDrawer?.hasAttribute('auto-open')) {
          //   this.#dispatchCartUpdate(cart);
          // } else {
          //   this.openCartDrawer();
          //   requestAnimationFrame(() => this.#dispatchCartUpdate(cart));
          // }
          this.#dispatchCartUpdate(cart);
          cartUpdated = true;

          setTimeout(() => this.#hideTooltip(), 3000);
        } else {
          // Real error — network failure, variant not found, etc.
          setTimeout(() => this.#hideTooltip(), 1500);
        }
      }

      await animationDone;
      this.isAdding = false;
      if (cartUpdated) this.qtyInput.value = 1;
    }

    /**
     * Flies the card's product image from the Add to cart button to the header
     * cart icon - the same <fly-to-cart> custom element the PDP add-to-cart
     * button uses (assets/fly-to-cart.js), registered globally on every page.
     *
     * Uses the `--quick` easing variant, not `--main`: assets/product-form.js
     * itself only ever uses `--main` for the large single PDP button and
     * switches to `--quick` for compact grid/quick-add buttons. `--main`'s
     * x-timing curve (cubic-bezier(0.7, -5, 0.98, 0.5)) has a large negative
     * overshoot tuned for that big PDP travel distance; on a card sitting near
     * the edge of the viewport (e.g. carousel slides) that overshoot pushes the
     * flying image past the screen edge before it swings back to the cart icon.
     * `--quick`'s curve (cubic-bezier(0, -0.1, 1, 0.32)) has a negligible
     * overshoot and stays on-screen regardless of card position.
     */
    #animateFlyToCart() {
      const cartIcon = document.querySelector('.header-actions__cart-icon');
      const image = this.mainImage?.currentSrc || this.mainImage?.src;

      if (!cartIcon || !this.atcBtn || !image) return;

      const flyToCartElement = document.createElement('fly-to-cart');
      flyToCartElement.classList.add('fly-to-cart--quick');
      flyToCartElement.style.setProperty('background-image', `url(${image})`);
      flyToCartElement.style.setProperty('--start-opacity', '0');
      flyToCartElement.source = this.atcBtn;
      flyToCartElement.destination = cartIcon;

      document.body.appendChild(flyToCartElement);
    }

    /**
     * Plays the theme's built-in add-to-cart checkmark-burst animation
     * (same `data-added` mechanism as assets/product-form.js `animateAddToCart`)
     * and waits for it to finish before reverting.
     */
    async #playAddedAnimation() {
      this.atcBtn.dataset.added = 'true';
      await CustomProductCard.#onAnimationEnd(this.atcBtn);
      await new Promise((resolve) => setTimeout(resolve, 800));
      delete this.atcBtn.dataset.added;
    }

    /** @param {Element} element */
    static #onAnimationEnd(element) {
      const animations = element.getAnimations({ subtree: true });
      return Promise.allSettled(animations.map((animation) => animation.finished));
    }

    async refreshCartDrawer() {
      try {
        const cartItemsComponent = document.querySelector('cart-items-component[data-drawer]');
        if (!cartItemsComponent) {
          return this.updateCartCount();
        }

        const sectionId = cartItemsComponent.dataset.sectionId;
        
        const [sectionResponse, cartResponse] = await Promise.all([
          fetch(`${window.Shopify.routes.root}?section_id=${sectionId}`),
          fetch(window.Shopify.routes.root + 'cart.js')
        ]);
        
        const [html, cart] = await Promise.all([
          sectionResponse.text(),
          cartResponse.json()
        ]);
        
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        this.#syncCartDrawerMarkup(doc);
        return cart;

      } catch (error) {
        console.error('Cart refresh error:', error);
        return this.updateCartCount();
      }
    }

    async updateCartCount() {
      try {
        const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
        const cart = await cartResponse.json();

        return cart;

      } catch (error) {
        console.error('Cart count update error:', error);
        return null;
      }
    }

    #syncCartDrawerMarkup(doc) {
      const currentDrawerDialog = document.querySelector('.cart-drawer__dialog[data-testid="cart-drawer-dialog"]');
      const newDrawerDialog = doc.querySelector('.cart-drawer__dialog[data-testid="cart-drawer-dialog"]');
      const currentDrawerInner = currentDrawerDialog?.querySelector('.cart-drawer__inner[data-hydration-key="cart-drawer-inner"]');
      const newDrawerInner = newDrawerDialog?.querySelector('.cart-drawer__inner[data-hydration-key="cart-drawer-inner"]');

      if (currentDrawerInner && newDrawerInner) {
        currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
      }

      if (!(currentDrawerDialog instanceof HTMLDialogElement) || !(newDrawerDialog instanceof HTMLDialogElement)) return;

      const wasOpen = currentDrawerDialog.open;

      for (const attr of Array.from(currentDrawerDialog.attributes)) {
        if (attr.name === 'open') continue;
        if (!newDrawerDialog.hasAttribute(attr.name)) {
          currentDrawerDialog.removeAttribute(attr.name);
        }
      }

      currentDrawerDialog.className = newDrawerDialog.className;

      for (const attr of Array.from(newDrawerDialog.attributes)) {
        if (attr.name === 'class' || attr.name === 'open') continue;
        currentDrawerDialog.setAttribute(attr.name, attr.value);
      }

      if (wasOpen) {
        currentDrawerDialog.setAttribute('open', '');
      } else {
        currentDrawerDialog.removeAttribute('open');
      }
    }

    #dispatchCartUpdate(cart) {
      if (!cart) return;

      // tt-custom-code for : stop - cart drawer open default when ATC
      // cart-drawer-component listens globally for this same 'cart:update' event
      // and opens itself when it has the 'auto-open' attribute. Drop the attribute
      // for this dispatch only, then restore it so every other add-to-cart flow
      // on the site keeps opening the drawer as normal.
      const cartDrawer = document.querySelector('cart-drawer-component');
      const hadAutoOpen = cartDrawer !== null && cartDrawer.hasAttribute('auto-open');
      if (cartDrawer && hadAutoOpen) cartDrawer.removeAttribute('auto-open');

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          detail: {
            resource: cart,
            sourceId: this.card.dataset.productId || '',
            data: {
              source: 'custom-product-card',
              itemCount: cart.item_count,
              variantId: this.atcBtn?.dataset.variantId || '',
            },
          },
        })
      );

      if (cartDrawer && hadAutoOpen) cartDrawer.setAttribute('auto-open', '');
    }

    openCartDrawer() {
      const cartDrawer = document.querySelector('cart-drawer-component');
      
      if (cartDrawer) {
        if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
          return;
        }
        
        if (typeof cartDrawer.showDialog === 'function') {
          cartDrawer.showDialog();
          return;
        }
      }
      
      const cartTrigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
      if (cartTrigger) {
        cartTrigger.click();
      }
    }
  }

  // ========================================
  // Initialize all product cards
  // ========================================
  
  function initProductCards() {
    const cards = document.querySelectorAll('.solution-card[data-card-behavior="custom-product-card"]');
    cards.forEach(card => {
      if (card.dataset.cardInitialized === 'true') return;
      new CustomProductCard(card);
      card.dataset.cardInitialized = 'true';
    });
  }

  // ========================================
  // Cross-card pincode sync
  // ========================================
  // Verifying a pincode via the popup on ANY card only needs to write once to
  // localStorage (assets/pincode-gate.js); every other gated card on the page
  // reads that same storage live now (see isPincodeVerified above), so a click
  // already works everywhere without this. This listener just keeps the visible
  // labels in sync immediately, instead of only updating once a shopper happens
  // to click each card's button. It runs both ways: a verified pincode unlocks
  // every gated card, and a later *different* pincode failing (see
  // assets/pincode-gate.js clearVerifiedPincode) re-locks them all again -
  // trusting only the live localStorage state, never a stale one-time read.
  function syncGatedCardButtons() {
    const verified = Boolean(window.PincodeGate?.getVerifiedPincode?.());
    document.querySelectorAll('.solution-card[data-pincode-gated="true"] .atc-btn').forEach(btn => {
      if (btn.disabled) return;
      const textEl = btn.querySelector('.add-to-cart-text');
      if (!textEl) return;
      const label = textEl.textContent.trim();
      if (verified && label === 'Check Availability') {
        textEl.textContent = 'Add to cart';
      } else if (!verified && label === 'Add to cart') {
        textEl.textContent = 'Check Availability';
      }
    });
  }

  document.addEventListener('pincode-gate:verified', syncGatedCardButtons);

  // A page the shopper navigated away from (e.g. to check a pincode on a PDP)
  // can come back via the browser's back/forward cache instead of a real
  // reload - no script re-runs, so a card left showing "Check Availability"
  // would otherwise stay stuck that way even though localStorage was updated
  // on the page they just came back from. `pageshow` with `persisted: true`
  // is how a bfcache restore is detected.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) syncGatedCardButtons();
  });

  // window.PincodeGate is set by a *module* script (pincode-gate-dialog.js),
  // while this file is a classic `defer` script - those are two independent
  // execution queues with no guaranteed order between them, so on some loads
  // this script can construct cards before window.PincodeGate exists yet.
  // Each card's one-time constructor check would then lock in the wrong
  // "Check Availability" label forever (the click handler still works, since
  // it re-checks live, but the label never corrects itself). `load` fires
  // only after every script of every kind has finished executing, so it's a
  // guaranteed final correction pass regardless of that ordering race.
  if (document.readyState === 'complete') {
    syncGatedCardButtons();
  } else {
    window.addEventListener('load', syncGatedCardButtons);
  }

  // ========================================
  // Initial load
  // ========================================
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductCards);
  } else {
    initProductCards();
  }

  // ========================================
  // Re-initialize for dynamic content
  // ========================================
  
  document.addEventListener('results-list:updated', initProductCards);
  document.addEventListener('product-recommendations:loaded', () => setTimeout(initProductCards, 100));

  const productRecommendations = document.querySelector('product-recommendations');
  if (productRecommendations) {
    new MutationObserver((mutations) => {
      if (mutations.some(m => m.addedNodes.length > 0)) setTimeout(initProductCards, 100);
    }).observe(productRecommendations, { childList: true, subtree: true });
  }

  let checkCount = 0;
  const checkInterval = setInterval(() => {
    const uninit = document.querySelectorAll('.solution-card:not([data-card-initialized="true"])');
    if (uninit.length > 0) initProductCards();
    if (++checkCount >= 5) clearInterval(checkInterval);
  }, 2000);

})();
